import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Trash2 } from 'lucide-react'
import { getCreditNote, createCreditNote, updateCreditNote, getCustomers, getProducts, getInvoices } from '../services/api'
import { calculateLineItemTax, calculateInvoiceTotals } from '../lib/gst'
import { formatCurrency, GST_STATES, toInputDate } from '../lib/formatters'
import SearchableSelect from '../components/ui/SearchableSelect'

const EMPTY_LINE = { productId: '', description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18 }

export default function CreditNoteForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [form, setForm] = useState({
    customerId: '', linkedInvoiceId: '', creditNoteDate: toInputDate(new Date()),
    placeOfSupply: '', currency: 'INR', reason: '', notes: '',
  })
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }])
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [invoices, setInvoices] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      getCustomers({ take: 2000 }),
      getProducts({ take: 500 }),
      getInvoices({ status: 'APPROVED', take: 500 }),
      isEdit ? getCreditNote(id) : null,
    ]).then(([custRes, prodRes, invRes, cnRes]) => {
      const custData = custRes.data
      setCustomers(Array.isArray(custData) ? custData : custData.customers || [])
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : prodRes.data.products || [])
      setInvoices(invRes.data?.invoices || invRes.data || [])
      if (cnRes) {
        const cn = cnRes.data
        setForm({
          customerId: cn.customerId, linkedInvoiceId: cn.linkedInvoiceId || '',
          creditNoteDate: toInputDate(cn.date), placeOfSupply: cn.placeOfSupply?.toString() || '',
          currency: cn.currency, reason: cn.reason || '', notes: cn.notes || '',
        })
        setLineItems(cn.lineItems.map((li) => ({
          productId: li.productServiceId || '', description: li.description, hsnSac: li.hsnSacCode || '',
          quantity: li.qty, rate: Number(li.rate), gstRate: Number(li.gstRate),
        })))
      }
    }).catch(() => {})
  }, [id, isEdit])

  const handleCustomerChange = (customerId) => {
    setForm((f) => ({ ...f, customerId }))
    const cust = customers.find((c) => c.id === customerId)
    if (cust?.gstStateCode) setForm((f) => ({ ...f, customerId, placeOfSupply: cust.gstStateCode.toString() }))
  }

  const handleProductSelect = (index, productId) => {
    const prod = products.find((p) => p.id === productId)
    if (prod) updateLine(index, { productId, description: prod.name, hsnSac: prod.hsnSacCode || '', rate: Number(prod.defaultRate), gstRate: Number(prod.gstRate) })
    else updateLine(index, { productId })
  }

  const updateLine = (index, changes) => setLineItems((prev) => prev.map((li, i) => i === index ? { ...li, ...changes } : li))
  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE }])
  const removeLine = (index) => setLineItems((prev) => prev.filter((_, i) => i !== index))

  // Tax-inclusive: rate includes GST
  const computedLines = lineItems.map((li) => {
    const inclusiveAmount = li.quantity * li.rate
    const tax = calculateLineItemTax(li.gstRate, inclusiveAmount, form.placeOfSupply)
    return { ...li, ...tax, total: inclusiveAmount }
  })
  const totals = calculateInvoiceTotals(computedLines)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        customerId: form.customerId, linkedInvoiceId: form.linkedInvoiceId || null,
        date: form.creditNoteDate, placeOfSupply: form.placeOfSupply ? Number(form.placeOfSupply) : null,
        currency: form.currency, reason: form.reason, notes: form.notes,
        lineItems: computedLines.map((li) => ({
          productServiceId: li.productId || null, description: li.description, hsnSacCode: li.hsnSac,
          qty: li.quantity, rate: li.rate, gstRate: li.gstRate, taxableAmount: li.taxableAmount,
          cgst: li.cgst, sgst: li.sgst, igst: li.igst, total: li.total,
        })),
      }
      if (isEdit) { await updateCreditNote(id, payload); navigate(`/credit-notes`) }
      else { await createCreditNote(payload); navigate('/credit-notes') }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save')
    } finally { setSaving(false) }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">{isEdit ? 'Edit Credit Note' : 'New Credit Note'}</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        <div className="card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="label">Customer</label>
            <SearchableSelect
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
              value={form.customerId}
              onChange={handleCustomerChange}
              placeholder="Select customer"
              required
            />
          </div>
          <div>
            <label className="label">Linked Invoice (optional)</label>
            <select className="select" value={form.linkedInvoiceId} onChange={(e) => setForm({ ...form, linkedInvoiceId: e.target.value })}>
              <option value="">None</option>
              {invoices.filter((i) => !form.customerId || i.customerId === form.customerId).map((i) => (
                <option key={i.id} value={i.id}>{i.number || 'DRAFT'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Date</label>
            <input type="date" className="input" value={form.creditNoteDate} onChange={(e) => setForm({ ...form, creditNoteDate: e.target.value })} onKeyDown={(e) => e.preventDefault()} required />
          </div>
          <div>
            <label className="label">Place of Supply</label>
            <SearchableSelect
              options={GST_STATES.map((s) => ({ value: String(s.code), label: `${s.code} - ${s.name}` }))}
              value={form.placeOfSupply}
              onChange={(val) => setForm({ ...form, placeOfSupply: val })}
              placeholder="Select state"
            />
          </div>
          <div>
            <label className="label">Reason</label>
            <input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Rate correction" />
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Line Items</h2>
          <div className="space-y-3">
            {lineItems.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  {i === 0 && <label className="label">Product / Service</label>}
                  <select className="select" value={li.productId} onChange={(e) => handleProductSelect(i, e.target.value)}>
                    <option value="">Select...</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="col-span-3">{i === 0 && <label className="label">Description</label>}<input className="input" value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })} required /></div>
                <div className="col-span-1">{i === 0 && <label className="label">HSN/SAC</label>}<input className="input" value={li.hsnSac} onChange={(e) => updateLine(i, { hsnSac: e.target.value })} /></div>
                <div className="col-span-1">{i === 0 && <label className="label">Qty</label>}<input type="number" min="1" className="input" value={li.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} /></div>
                <div className="col-span-1">{i === 0 && <label className="label">Rate</label>}<input type="number" min="0" step="0.01" className="input" value={li.rate} onChange={(e) => updateLine(i, { rate: Number(e.target.value) })} /></div>
                <div className="col-span-1">{i === 0 && <label className="label">GST %</label>}
                  <select className="select" value={li.gstRate} onChange={(e) => updateLine(i, { gstRate: Number(e.target.value) })}>
                    <option value={0}>0%</option><option value={5}>5%</option><option value={12}>12%</option><option value={18}>18%</option><option value={28}>28%</option>
                  </select>
                </div>
                <div className="col-span-1 text-right text-sm font-medium pt-1">{i === 0 && <label className="label">Amount</label>}{formatCurrency(computedLines[i]?.total || 0, form.currency)}</div>
                <div className="col-span-1 flex justify-end">{lineItems.length > 1 && <button type="button" onClick={() => removeLine(i)} className="p-1.5 text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}</div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="mt-3 btn-ghost flex items-center gap-1.5 text-sm"><Plus className="h-4 w-4" /> Add Line</button>
        </div>

        <div className="card">
          <div className="flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(totals.subtotal, form.currency)}</span></div>
              {totals.cgst > 0 && <div className="flex justify-between"><span className="text-gray-500">CGST</span><span>{formatCurrency(totals.cgst, form.currency)}</span></div>}
              {totals.sgst > 0 && <div className="flex justify-between"><span className="text-gray-500">SGST</span><span>{formatCurrency(totals.sgst, form.currency)}</span></div>}
              {totals.igst > 0 && <div className="flex justify-between"><span className="text-gray-500">IGST</span><span>{formatCurrency(totals.igst, form.currency)}</span></div>}
              <div className="flex justify-between font-semibold text-base border-t pt-2"><span>Total</span><span>{formatCurrency(totals.total, form.currency)}</span></div>
            </div>
          </div>
        </div>

        <div className="card">
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : isEdit ? 'Update' : 'Create Draft'}</button>
        </div>
      </form>
    </div>
  )
}
